import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePlatformStore } from '@/stores/platform'
import { isPlatformHost, tenantBase } from '@/config/host'

const MANAGE_STUDENTS = ['super_admin', 'school_admin', 'principal']
// Principal has full access to the People + Academics modules alongside admins.
const MANAGE_CLASSES = ['super_admin', 'school_admin', 'principal']
const MANAGE_SUBJECTS = ['super_admin', 'school_admin', 'principal']
const MANAGE_SESSIONS = ['super_admin', 'principal']
const MANAGE_FINANCE = ['super_admin', 'finance_officer']
const MANAGE_ATTENDANCE = ['super_admin', 'school_admin', 'principal']
const MANAGE_TEACHERS = ['super_admin', 'school_admin', 'principal']
// Principal/head teacher oversees + approves results across the school...
const MANAGE_RESULTS = ['super_admin', 'school_admin', 'principal']
// Exam setup (assessment components, form-teacher assignment) — principal included.
const MANAGE_RESULT_SETUP = ['super_admin', 'school_admin', 'principal']
// CBT authoring — admins/principal plus teachers (scoped to their class+subject server-side).
const MANAGE_CBT = ['super_admin', 'school_admin', 'principal', 'teacher']
const MANAGE_PARENTS = ['super_admin', 'school_admin', 'principal']
// finance_officer gets read-only access to the parents list + detail (wallet balances)
const VIEW_PARENTS = [...MANAGE_PARENTS, 'finance_officer']
const MANAGE_EMPLOYEES = ['super_admin', 'school_admin']
const MANAGE_PAYROLL = ['super_admin', 'finance_officer']
const MANAGE_DOCUMENTS = ['super_admin', 'school_admin']
const MANAGE_LIBRARY = ['super_admin', 'school_admin', 'librarian']
const PARENT_PORTAL = ['parent']
const STUDENT_PORTAL = ['student']
const TEACHER_PORTAL = ['teacher']
const MANAGE_SETTINGS = ['super_admin']
const MANAGE_CELEBRATIONS = ['super_admin', 'school_admin']
const MANAGE_INVENTORY = ['super_admin', 'school_admin']
const MANAGE_TRANSPORT = ['super_admin', 'school_admin']
const MANAGE_HOSTEL = ['super_admin', 'school_admin']
const MANAGE_VISITORS = ['super_admin', 'school_admin']
// Anyone who signs report cards: form teachers + the principal/admin approvers.
const SIGN_RESULTS = ['teacher', 'super_admin', 'school_admin', 'principal']

// The apex host (skulkit.com / www / localhost) serves the SaaS platform
// marketing site, managed by the platform admin. A school's subdomain
// (greenwood.skulkit.com) serves that school's own CMS website, managed by its
// super admin. The host is fixed for the life of the page, so we pick one tree
// at module load.
const rootRoute = isPlatformHost()
  ? {
      path: '/',
      component: () => import('@/views/platform-site/PlatformSite.vue'),
      children: [
        { path: '', name: 'platform-home', component: () => import('@/views/platform-site/PlatformHomeView.vue') },
        { path: 'features', name: 'platform-features', component: () => import('@/views/platform-site/FeaturesView.vue') },
        {
          path: 'products/:slug',
          name: 'platform-product',
          component: () => import('@/views/platform-site/ProductView.vue'),
          props: (route) => ({ productKey: route.params.slug }),
        },
        { path: 'pricing', name: 'platform-pricing', component: () => import('@/views/platform-site/PricingView.vue') },
        { path: 'about', name: 'platform-about', component: () => import('@/views/platform-site/AboutView.vue') },
        { path: 'services', name: 'platform-services', component: () => import('@/views/platform-site/ServicesView.vue') },
        { path: 'contact', name: 'platform-contact', component: () => import('@/views/platform-site/ContactView.vue') },
        { path: 'faq', name: 'platform-faq', component: () => import('@/views/platform-site/FaqView.vue') },
        { path: 'docs', name: 'platform-docs', component: () => import('@/views/platform-site/DocsView.vue') },
        {
          path: 'docs/:role',
          name: 'platform-docs-role',
          component: () => import('@/views/platform-site/DocsView.vue'),
          props: (route) => ({ role: route.params.role }),
        },
        { path: 'blog', name: 'platform-blog', component: () => import('@/views/platform-site/PostListView.vue') },
        { path: 'blog/:slug', name: 'platform-post-detail', component: () => import('@/views/platform-site/PostDetailView.vue') },
        { path: 'insights', redirect: { name: 'platform-blog' } },
        {
          path: 'privacy',
          name: 'platform-privacy',
          component: () => import('@/views/platform-site/LegalPageView.vue'),
          props: { contentKey: 'privacy' },
        },
        {
          path: 'terms',
          name: 'platform-terms',
          component: () => import('@/views/platform-site/LegalPageView.vue'),
          props: { contentKey: 'terms' },
        },
        {
          path: 'data-deletion',
          name: 'platform-data-deletion',
          component: () => import('@/views/platform-site/DataDeletionView.vue'),
        },
        {
          path: ':pathMatch(.*)*',
          name: 'platform-not-found',
          component: () => import('@/views/platform-site/NotFoundView.vue'),
        },
      ],
    }
  : {
      path: '/',
      component: () => import('@/views/website/PublicSite.vue'),
      children: [
        { path: '', name: 'site-home', component: () => import('@/views/website/HomeView.vue') },
        { path: 'about', name: 'site-about', component: () => import('@/views/website/AboutView.vue') },
        { path: 'academics', name: 'site-academics', component: () => import('@/views/website/AcademicsView.vue') },
        { path: 'news', name: 'site-news', component: () => import('@/views/website/NewsListView.vue') },
        { path: 'news/:slug', name: 'site-news-detail', component: () => import('@/views/website/NewsDetailView.vue') },
        { path: 'events', name: 'site-events', component: () => import('@/views/website/EventsView.vue') },
        { path: 'gallery', name: 'site-gallery', component: () => import('@/views/website/GalleryView.vue') },
        { path: 'contact', name: 'site-contact', component: () => import('@/views/website/ContactView.vue') },
        {
          path: 'privacy',
          name: 'site-privacy',
          component: () => import('@/views/website/LegalPageView.vue'),
          props: { title: 'Privacy Policy', contentKey: 'privacy_policy' },
        },
        {
          path: 'terms',
          name: 'site-terms',
          component: () => import('@/views/website/LegalPageView.vue'),
          props: { title: 'Terms of Use', contentKey: 'terms_of_use' },
        },
        {
          path: ':pathMatch(.*)*',
          name: 'site-not-found',
          component: () => import('@/views/website/NotFoundView.vue'),
        },
      ],
    }

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true, portal: 'staff' },
  },
  {
    path: '/student/login',
    name: 'student-login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true, portal: 'student' },
  },
  {
    path: '/parent/login',
    name: 'parent-login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true, portal: 'parent' },
  },
  {
    path: '/apply',
    name: 'admission-apply',
    component: () => import('@/views/admissions/AdmissionApplyView.vue'),
  },
  {
    path: '/staff-checkin',
    name: 'staff-checkin',
    component: () => import('@/views/staff-attendance/StaffCheckinView.vue'),
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/auth/SignupView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/cbt/:token',
    name: 'public-cbt',
    component: () => import('@/views/cbt/PublicCbtView.vue'),
  },
  {
    path: '/verify/receipt/:number',
    name: 'receipt-verify',
    component: () => import('@/views/ReceiptVerifyView.vue'),
  },
  {
    path: '/verify/result/:code',
    name: 'result-verify',
    component: () => import('@/views/ResultVerifyView.vue'),
  },
  {
    path: '/pay/callback',
    name: 'pay-callback',
    component: () => import('@/views/finance/PayCallbackView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/platform/login',
    name: 'platform-login',
    component: () => import('@/views/platform/PlatformLoginView.vue'),
  },
  {
    path: '/platform/forgot-password',
    name: 'platform-forgot-password',
    component: () => import('@/views/platform/PlatformForgotPasswordView.vue'),
  },
  {
    path: '/platform/reset-password',
    name: 'platform-reset-password',
    component: () => import('@/views/platform/PlatformResetPasswordView.vue'),
  },
  {
    path: '/platform',
    component: () => import('@/layouts/PlatformLayout.vue'),
    meta: { platform: true },
    children: [
      { path: '', redirect: { name: 'platform-dashboard' } },
      {
        path: 'dashboard',
        name: 'platform-dashboard',
        component: () => import('@/views/platform/PlatformDashboardView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'schools',
        name: 'platform-schools',
        component: () => import('@/views/platform/PlatformSchoolsView.vue'),
        meta: { title: 'Schools' },
      },
      {
        path: 'schools/:id',
        name: 'platform-school-detail',
        component: () => import('@/views/platform/PlatformSchoolDetailView.vue'),
        meta: { title: 'School Report' },
      },
      {
        path: 'plans',
        name: 'platform-plans',
        component: () => import('@/views/platform/PlatformPlansView.vue'),
        meta: { title: 'Plans & Packages' },
      },
      {
        path: 'transactions',
        name: 'platform-transactions',
        component: () => import('@/views/platform/PlatformTransactionsView.vue'),
        meta: { title: 'Transactions' },
      },
      {
        path: 'messages',
        name: 'platform-messages',
        component: () => import('@/views/platform/PlatformMessagesView.vue'),
        meta: { title: 'Contact Messages' },
      },
      {
        path: 'data-requests',
        name: 'platform-data-requests',
        component: () => import('@/views/platform/PlatformDataRequestsView.vue'),
        meta: { title: 'Data Deletion Requests' },
      },
      {
        path: 'payment-settings',
        name: 'platform-payment-settings',
        component: () => import('@/views/platform/PlatformPaymentSettingsView.vue'),
        meta: { title: 'Payment Integrations' },
      },
      {
        path: 'website',
        name: 'platform-website',
        component: () => import('@/views/platform/PlatformWebsiteView.vue'),
        meta: { title: 'Marketing Website' },
      },
      {
        path: 'blog',
        name: 'platform-blog-manage',
        component: () => import('@/views/platform/PlatformBlogView.vue'),
        meta: { title: 'Blog & Insights' },
      },
      {
        path: 'branding',
        name: 'platform-branding',
        component: () => import('@/views/platform/PlatformBrandingView.vue'),
        meta: { title: 'Branding' },
      },
      {
        path: 'activity',
        name: 'platform-activity',
        component: () => import('@/views/platform/PlatformAuditView.vue'),
        meta: { title: 'Activity Log' },
      },
      {
        path: 'backups',
        name: 'platform-backups',
        component: () => import('@/views/platform/PlatformBackupsView.vue'),
        meta: { title: 'Backup & Restore' },
      },
    ],
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { guestOnly: true },
  },
  rootRoute,
  {
    path: '/portal',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: () => useAuthStore().homeRoute },
      { path: 'upgrade', name: 'upgrade', component: () => import('@/views/UpgradeView.vue') },
      { path: 'billing', name: 'billing', component: () => import('@/views/billing/BillingView.vue') },
      { path: 'billing/callback', name: 'billing-callback', component: () => import('@/views/billing/BillingCallbackView.vue') },
      { path: 'change-password', name: 'change-password', component: () => import('@/views/auth/ChangePasswordView.vue') },
      { path: 'announcements', name: 'announcements', component: () => import('@/views/communication/AnnouncementsView.vue') },
      { path: 'messages', name: 'messages', component: () => import('@/views/communication/MessagesView.vue') },

      { path: 'admin/dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
      {
        path: 'admin/students',
        name: 'students',
        component: () => import('@/views/students/StudentListView.vue'),
        meta: { roles: MANAGE_STUDENTS },
      },
      {
        path: 'admin/admissions',
        name: 'admissions',
        component: () => import('@/views/admissions/AdmissionsListView.vue'),
        meta: { roles: MANAGE_STUDENTS },
      },
      {
        path: 'admin/students/new',
        name: 'student-create',
        component: () => import('@/views/students/StudentFormView.vue'),
        meta: { roles: MANAGE_STUDENTS },
      },
      {
        path: 'admin/students/:id',
        name: 'student-detail',
        component: () => import('@/views/students/StudentDetailView.vue'),
        meta: { roles: MANAGE_STUDENTS },
      },
      {
        path: 'admin/students/:id/edit',
        name: 'student-edit',
        component: () => import('@/views/students/StudentFormView.vue'),
        meta: { roles: MANAGE_STUDENTS },
      },
      {
        path: 'admin/teachers',
        name: 'teachers',
        component: () => import('@/views/teachers/TeacherListView.vue'),
        meta: { roles: MANAGE_TEACHERS },
      },
      {
        path: 'admin/teachers/new',
        name: 'teacher-create',
        component: () => import('@/views/teachers/TeacherFormView.vue'),
        meta: { roles: MANAGE_TEACHERS },
      },
      {
        path: 'admin/teachers/:id',
        name: 'teacher-detail',
        component: () => import('@/views/teachers/TeacherDetailView.vue'),
        meta: { roles: MANAGE_TEACHERS },
      },
      {
        path: 'admin/teachers/:id/edit',
        name: 'teacher-edit',
        component: () => import('@/views/teachers/TeacherFormView.vue'),
        meta: { roles: MANAGE_TEACHERS },
      },
      {
        path: 'admin/employees',
        name: 'employees',
        component: () => import('@/views/employees/EmployeeListView.vue'),
        meta: { roles: MANAGE_EMPLOYEES },
      },
      {
        path: 'admin/employees/new',
        name: 'employee-create',
        component: () => import('@/views/employees/EmployeeFormView.vue'),
        meta: { roles: MANAGE_EMPLOYEES },
      },
      {
        path: 'admin/employees/:id',
        name: 'employee-detail',
        component: () => import('@/views/employees/EmployeeDetailView.vue'),
        meta: { roles: MANAGE_EMPLOYEES },
      },
      {
        path: 'admin/employees/:id/edit',
        name: 'employee-edit',
        component: () => import('@/views/employees/EmployeeFormView.vue'),
        meta: { roles: MANAGE_EMPLOYEES },
      },
      {
        path: 'admin/payroll',
        name: 'payroll',
        component: () => import('@/views/payroll/PayrollListView.vue'),
        meta: { roles: MANAGE_PAYROLL },
      },
      {
        path: 'admin/payroll/:id',
        name: 'payroll-run',
        component: () => import('@/views/payroll/PayrollRunView.vue'),
        meta: { roles: MANAGE_PAYROLL },
      },
      {
        path: 'admin/parents',
        name: 'parents',
        component: () => import('@/views/parents/ParentListView.vue'),
        meta: { roles: VIEW_PARENTS },
      },
      {
        path: 'admin/parents/new',
        name: 'parent-create',
        component: () => import('@/views/parents/ParentFormView.vue'),
        meta: { roles: MANAGE_PARENTS },
      },
      {
        path: 'admin/parents/:id',
        name: 'parent-detail',
        component: () => import('@/views/parents/ParentDetailView.vue'),
        meta: { roles: VIEW_PARENTS },
      },
      {
        path: 'admin/parents/:id/edit',
        name: 'parent-edit',
        component: () => import('@/views/parents/ParentFormView.vue'),
        meta: { roles: MANAGE_PARENTS },
      },
      {
        path: 'parents/children',
        name: 'my-children',
        component: () => import('@/views/portal/MyChildrenView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'parents/pay',
        name: 'parent-make-payment',
        component: () => import('@/views/portal/MakePaymentView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'parents/wallet',
        name: 'parent-wallet',
        component: () => import('@/views/portal/ParentWalletView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'parents/children/:id',
        name: 'child-detail',
        component: () => import('@/views/portal/ChildDetailView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'parents/children/:studentId/invoices/:invoiceId',
        name: 'parent-portal-invoice',
        component: () => import('@/views/portal/PortalInvoiceView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'parents/result-sheet',
        name: 'parent-result-sheet',
        component: () => import('@/views/results/ResultSheetView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'students/fees',
        name: 'my-fees',
        component: () => import('@/views/portal/student/StudentFeesView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/attendance',
        name: 'my-attendance',
        component: () => import('@/views/portal/student/StudentAttendanceView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/results',
        name: 'my-results',
        component: () => import('@/views/portal/student/StudentResultsView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/timetable',
        name: 'my-timetable',
        component: () => import('@/views/portal/student/StudentTimetableView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/exams',
        name: 'my-exams',
        component: () => import('@/views/portal/student/StudentExamsView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/cbt',
        name: 'student-cbt',
        component: () => import('@/views/portal/student/StudentCbtListView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/cbt/:id',
        name: 'student-cbt-take',
        component: () => import('@/views/portal/student/StudentCbtExamView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/cbt/result/:attemptId',
        name: 'student-cbt-result',
        component: () => import('@/views/portal/student/StudentCbtResultView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/invoices/:invoiceId',
        name: 'student-portal-invoice',
        component: () => import('@/views/portal/PortalInvoiceView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/result-sheet',
        name: 'student-result-sheet',
        component: () => import('@/views/results/ResultSheetView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'students/assignments',
        name: 'student-assignments',
        component: () => import('@/views/portal/PortalAssignmentsView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'parents/children/:studentId/assignments',
        name: 'child-assignments',
        component: () => import('@/views/portal/PortalAssignmentsView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'students/library',
        name: 'student-library',
        component: () => import('@/views/portal/PortalLibraryView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'parents/children/:studentId/library',
        name: 'child-library',
        component: () => import('@/views/portal/PortalLibraryView.vue'),
        meta: { roles: PARENT_PORTAL },
      },
      {
        path: 'admin/library/books',
        name: 'library-books',
        component: () => import('@/views/library/LibraryBooksView.vue'),
        meta: { roles: MANAGE_LIBRARY },
      },
      {
        path: 'teacher/classes',
        name: 'teacher-classes',
        component: () => import('@/views/teacher/TeacherClassesView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/results',
        name: 'teacher-results',
        component: () => import('@/views/teacher/TeacherResultsView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/class-review',
        name: 'teacher-class-review',
        component: () => import('@/views/teacher/TeacherClassReviewView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/result-sheet',
        name: 'teacher-result-sheet',
        component: () => import('@/views/results/ResultSheetView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/attendance',
        name: 'teacher-attendance',
        component: () => import('@/views/teacher/TeacherAttendanceView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/assignments',
        name: 'teacher-assignments',
        component: () => import('@/views/teacher/TeacherAssignmentsView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/gradebook',
        name: 'teacher-gradebook',
        component: () => import('@/views/teacher/TeacherGradebookView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'teacher/lesson-notes',
        name: 'teacher-lesson-notes',
        component: () => import('@/views/teacher/TeacherLessonNotesView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'admin/lesson-notes',
        name: 'lesson-notes-review',
        component: () => import('@/views/lessonNotes/LessonNotesReviewView.vue'),
        meta: { roles: MANAGE_RESULTS },
      },
      {
        path: 'admin/newsletters',
        name: 'newsletters',
        component: () => import('@/views/newsletters/NewslettersView.vue'),
        meta: { roles: MANAGE_RESULTS },
      },
      {
        path: 'teacher/newsletters',
        name: 'teacher-newsletters',
        component: () => import('@/views/portal/PortalNewslettersView.vue'),
        meta: { roles: TEACHER_PORTAL },
      },
      {
        path: 'students/newsletters',
        name: 'student-newsletters',
        component: () => import('@/views/portal/PortalNewslettersView.vue'),
        meta: { roles: STUDENT_PORTAL },
      },
      {
        path: 'my-signature',
        name: 'my-signature',
        component: () => import('@/views/results/MySignatureView.vue'),
        meta: { roles: SIGN_RESULTS },
      },
      {
        path: 'admin/academic/sessions',
        name: 'academic-sessions',
        component: () => import('@/views/academic/SessionsView.vue'),
        meta: { roles: MANAGE_SESSIONS },
      },
      {
        path: 'admin/academic/classes',
        name: 'academic-classes',
        component: () => import('@/views/academic/ClassesView.vue'),
        meta: { roles: MANAGE_CLASSES },
      },
      {
        path: 'admin/academic/promotions',
        name: 'academic-promotions',
        component: () => import('@/views/academic/PromotionsView.vue'),
        meta: { roles: MANAGE_CLASSES },
      },
      {
        path: 'admin/academic/departments',
        name: 'academic-departments',
        component: () => import('@/views/academic/DepartmentsView.vue'),
        meta: { roles: MANAGE_SUBJECTS },
      },
      {
        path: 'admin/academic/subjects',
        name: 'academic-subjects',
        component: () => import('@/views/academic/SubjectsView.vue'),
        meta: { roles: MANAGE_SUBJECTS },
      },
      {
        path: 'admin/academic/subject-assignment',
        name: 'academic-subject-assignment',
        component: () => import('@/views/academic/SubjectAssignmentView.vue'),
        meta: { roles: MANAGE_SUBJECTS },
      },
      {
        path: 'admin/academic/timetable',
        name: 'class-timetable',
        component: () => import('@/views/academic/ClassTimetableView.vue'),
        meta: { roles: MANAGE_CLASSES },
      },
      {
        path: 'admin/academic/teacher-timetable',
        name: 'teacher-timetable',
        component: () => import('@/views/academic/TeacherTimetableView.vue'),
        meta: { roles: MANAGE_CLASSES },
      },
      {
        path: 'admin/academic/exam-timetable',
        name: 'exam-timetable',
        component: () => import('@/views/academic/ExamTimetableView.vue'),
        meta: { roles: MANAGE_CLASSES },
      },
      {
        path: 'admin/finance/fee-categories',
        name: 'fee-categories',
        component: () => import('@/views/finance/FeeCategoriesView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/fee-assignment',
        name: 'fee-assignment',
        component: () => import('@/views/finance/FeeAssignmentView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/invoices',
        name: 'invoices',
        component: () => import('@/views/finance/InvoiceListView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/invoices/new',
        name: 'invoice-generate',
        component: () => import('@/views/finance/InvoiceGenerateView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/invoices/batch',
        name: 'invoice-batch',
        component: () => import('@/views/finance/InvoiceBatchView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/other-fee',
        name: 'other-fee',
        component: () => import('@/views/finance/SundryBillView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/invoices/:id',
        name: 'invoice-detail',
        component: () => import('@/views/finance/InvoiceDetailView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/payments',
        name: 'payments',
        component: () => import('@/views/finance/PaymentsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/refunds',
        name: 'refunds',
        component: () => import('@/views/finance/RefundsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/discounts',
        name: 'discounts',
        component: () => import('@/views/finance/DiscountsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/bank-approvals',
        name: 'bank-approvals',
        component: () => import('@/views/finance/BankApprovalsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/pending-payments',
        name: 'pending-payments',
        component: () => import('@/views/finance/PendingPaymentsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/reports',
        name: 'financial-reports',
        component: () => import('@/views/finance/ReportsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/journal',
        name: 'journal',
        component: () => import('@/views/finance/accounting/JournalView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/activity',
        name: 'account-activity',
        component: () => import('@/views/finance/accounting/AccountActivityView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/cashbook',
        name: 'cash-book',
        component: () => import('@/views/finance/accounting/CashBookView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/income-expenditure',
        name: 'income-expenditure',
        component: () => import('@/views/finance/accounting/IncomeExpenditureView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/budgets',
        name: 'budgets',
        component: () => import('@/views/finance/accounting/BudgetsView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/expenses',
        name: 'expenses',
        component: () => import('@/views/finance/accounting/ExpensesView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/finance/other-income',
        name: 'other-income',
        component: () => import('@/views/finance/accounting/OtherIncomeView.vue'),
        meta: { roles: MANAGE_FINANCE },
      },
      {
        path: 'admin/results/entry',
        name: 'result-entry',
        component: () => import('@/views/results/ResultEntryView.vue'),
        meta: { roles: MANAGE_RESULTS },
      },
      {
        path: 'admin/results',
        name: 'class-results',
        component: () => import('@/views/results/ClassResultsView.vue'),
        meta: { roles: MANAGE_RESULTS },
      },
      {
        path: 'admin/results/sheet',
        name: 'result-sheet',
        component: () => import('@/views/results/ResultSheetView.vue'),
        meta: { roles: MANAGE_RESULTS },
      },
      {
        path: 'admin/results/components',
        name: 'assessment-components',
        component: () => import('@/views/results/AssessmentComponentsView.vue'),
        meta: { roles: MANAGE_RESULT_SETUP },
      },
      {
        path: 'admin/results/class-teachers',
        name: 'class-teachers',
        component: () => import('@/views/results/ClassTeachersView.vue'),
        meta: { roles: MANAGE_RESULT_SETUP },
      },
      {
        path: 'admin/cbt/question-bank',
        name: 'cbt-question-bank',
        component: () => import('@/views/cbt/CbtQuestionBankView.vue'),
        meta: { roles: MANAGE_CBT },
      },
      {
        path: 'admin/cbt/exams',
        name: 'cbt-exams',
        component: () => import('@/views/cbt/CbtExamsView.vue'),
        meta: { roles: MANAGE_CBT },
      },
      {
        path: 'admin/cbt/exams/:id/build',
        name: 'cbt-exam-build',
        component: () => import('@/views/cbt/CbtExamBuilderView.vue'),
        meta: { roles: MANAGE_CBT },
      },
      {
        path: 'admin/cbt/exams/:id/results',
        name: 'cbt-exam-results',
        component: () => import('@/views/cbt/CbtExamResultsView.vue'),
        meta: { roles: MANAGE_CBT },
      },
      {
        path: 'admin/inventory/items',
        name: 'inventory-items',
        component: () => import('@/views/inventory/InventoryItemsView.vue'),
        meta: { roles: MANAGE_INVENTORY },
      },
      {
        path: 'admin/inventory/suppliers',
        name: 'inventory-suppliers',
        component: () => import('@/views/inventory/InventorySuppliersView.vue'),
        meta: { roles: MANAGE_INVENTORY },
      },
      {
        path: 'admin/inventory/movements',
        name: 'inventory-movements',
        component: () => import('@/views/inventory/InventoryMovementsView.vue'),
        meta: { roles: MANAGE_INVENTORY },
      },
      {
        path: 'admin/transport/vehicles',
        name: 'transport-vehicles',
        component: () => import('@/views/transport/TransportVehiclesView.vue'),
        meta: { roles: MANAGE_TRANSPORT },
      },
      {
        path: 'admin/transport/routes',
        name: 'transport-routes',
        component: () => import('@/views/transport/TransportRoutesView.vue'),
        meta: { roles: MANAGE_TRANSPORT },
      },
      {
        path: 'admin/transport/assignments',
        name: 'transport-assignments',
        component: () => import('@/views/transport/TransportAssignmentsView.vue'),
        meta: { roles: MANAGE_TRANSPORT },
      },
      {
        path: 'students/transport',
        name: 'student-transport',
        component: () => import('@/views/portal/student/StudentTransportView.vue'),
        meta: { roles: ['student'] },
      },
      {
        path: 'parents/transport',
        name: 'parent-transport',
        component: () => import('@/views/portal/ParentTransportView.vue'),
        meta: { roles: ['parent'] },
      },
      {
        path: 'admin/hostel/blocks',
        name: 'hostel-blocks',
        component: () => import('@/views/hostel/HostelsView.vue'),
        meta: { roles: MANAGE_HOSTEL },
      },
      {
        path: 'admin/hostel/rooms',
        name: 'hostel-rooms',
        component: () => import('@/views/hostel/HostelRoomsView.vue'),
        meta: { roles: MANAGE_HOSTEL },
      },
      {
        path: 'admin/hostel/allocations',
        name: 'hostel-allocations',
        component: () => import('@/views/hostel/HostelAllocationsView.vue'),
        meta: { roles: MANAGE_HOSTEL },
      },
      {
        path: 'students/hostel',
        name: 'student-hostel',
        component: () => import('@/views/portal/student/StudentHostelView.vue'),
        meta: { roles: ['student'] },
      },
      {
        path: 'parents/hostel',
        name: 'parent-hostel',
        component: () => import('@/views/portal/ParentHostelView.vue'),
        meta: { roles: ['parent'] },
      },
      {
        path: 'admin/visitors',
        name: 'visitors',
        component: () => import('@/views/visitors/VisitorLogView.vue'),
        meta: { roles: MANAGE_VISITORS },
      },
      {
        path: 'admin/data-requests',
        name: 'data-requests',
        component: () => import('@/views/compliance/DataRequestsView.vue'),
        meta: { roles: ['super_admin', 'school_admin', 'principal'] },
      },
      {
        path: 'admin/attendance/take',
        name: 'attendance-take',
        component: () => import('@/views/attendance/AttendanceTakeView.vue'),
        meta: { roles: MANAGE_ATTENDANCE },
      },
      {
        path: 'admin/attendance/report',
        name: 'attendance-report',
        component: () => import('@/views/attendance/AttendanceReportView.vue'),
        meta: { roles: MANAGE_ATTENDANCE },
      },
      {
        path: 'admin/staff-attendance/passcodes',
        name: 'staff-attendance-passcodes',
        component: () => import('@/views/staff-attendance/StaffPasscodesView.vue'),
        meta: { roles: MANAGE_ATTENDANCE },
      },
      {
        path: 'admin/staff-attendance/report',
        name: 'staff-attendance-report',
        component: () => import('@/views/staff-attendance/StaffAttendanceReportView.vue'),
        meta: { roles: MANAGE_ATTENDANCE },
      },
      {
        path: 'admin/users',
        name: 'users',
        component: () => import('@/views/users/UserListView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/roles',
        name: 'roles',
        component: () => import('@/views/system/RolesPermissionsView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/users/new',
        name: 'user-create',
        component: () => import('@/views/users/UserFormView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/users/:id/edit',
        name: 'user-edit',
        component: () => import('@/views/users/UserFormView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/notifications',
        name: 'notification-templates',
        component: () => import('@/views/settings/NotificationTemplatesView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/celebrations/birthdays',
        name: 'birthday-settings',
        component: () => import('@/views/celebrations/BirthdaySettingsView.vue'),
        meta: { roles: MANAGE_CELEBRATIONS },
      },
      {
        path: 'admin/celebrations/holidays',
        name: 'holidays',
        component: () => import('@/views/celebrations/HolidaysView.vue'),
        meta: { roles: MANAGE_CELEBRATIONS },
      },
      {
        path: 'admin/celebrations/report',
        name: 'celebration-report',
        component: () => import('@/views/celebrations/CelebrationReportView.vue'),
        meta: { roles: MANAGE_CELEBRATIONS },
      },
      {
        path: 'admin/website',
        name: 'website-cms',
        component: () => import('@/views/website/admin/WebsiteCmsView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/audit',
        name: 'audit',
        component: () => import('@/views/system/AuditLogView.vue'),
        meta: { roles: MANAGE_SETTINGS },
      },
      {
        path: 'admin/documents',
        name: 'documents',
        component: () => import('@/views/documents/DocumentsView.vue'),
        meta: { roles: MANAGE_DOCUMENTS },
      },
    ],
  },
]

// Path-based tenancy: inside a school the base is /school/{slug}/, so every
// root-relative route (/login, /portal/...) is prefixed automatically and the
// slug survives navigation + reloads. On the platform the base is the app root.
const router = createRouter({
  history: createWebHistory(tenantBase()),
  routes,
})

// Which plan feature a route path requires (mirrors the sidebar gating and the
// backend Plan::requireFeature gates in index.php). null = no feature needed.
function featureForPath(path) {
  if (path.includes('/cbt')) return 'cbt'
  if (path.includes('/inventory')) return 'inventory'
  if (path.includes('/transport')) return 'transport'
  if (path.includes('/hostel')) return 'hostel'
  if (path.includes('/library')) return 'library'
  if (path.includes('/payroll')) return 'payroll'
  if (path === '/portal/admin/website') return 'website'
  return null
}

router.beforeEach(async (to) => {
  // Platform console lives on its own scope=platform token, entirely separate
  // from tenant auth. Guard it independently.
  if (to.meta.platform) {
    return usePlatformStore().isAuthenticated ? true : { name: 'platform-login' }
  }

  const auth = useAuthStore()

  // A platform operator entering a tenant hands the tenant JWT over on the login
  // URL. Consume it, then land the operator inside the school.
  if (to.name === 'login' && to.query.impersonate) {
    const ok = await auth.setImpersonation(String(to.query.impersonate))
    if (ok) return auth.homeRoute
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.meta.guestOnly && auth.isAuthenticated) {
    return auth.homeRoute
  }
  if (to.meta.roles && !to.meta.roles.includes(auth.role)) {
    return auth.homeRoute
  }
  const feature = to.meta.requiredFeature || featureForPath(to.path)
  if (feature && !auth.hasFeature(feature)) {
    return { name: 'upgrade', query: { feature } }
  }
  return true
})

export default router
